// =============================================================================
// OAUTH BUTTONS COMPONENT - FACEBOOK ACTUALIZADO PARA REACT 18
// =============================================================================
// Ubicación: src/components/Auth/OAuthButtons.tsx
// =============================================================================
// REEMPLAZA el archivo FRONTEND_03_OAuthButtons.tsx con este
// =============================================================================

import React, { useState } from 'react';
import { Button, Space, Divider } from 'antd';
import { message } from '../../services/toastMessage';
import { GoogleOutlined, GithubOutlined } from '@ant-design/icons';
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import { useMsal } from '@azure/msal-react';
import FacebookLogin from '@greatsumini/react-facebook-login'; // ← NUEVO PAQUETE
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  loginWithGoogle,
  loginWithMicrosoft,
  loginWithFacebook,
  getGitHubAuthUrl
} from '../../services/OAuthService';
import { useAuth } from '../../contextProviders/AuthProvider';
import { translate } from '../../helpers/translationHelper';
import { TRANSLATIONS } from '../../constants/translations';

// =================================================================
// MICROSOFT ICON
// =================================================================
const MicrosoftIcon = () => (
  <svg width="1em" height="1em" viewBox="0 0 21 21" fill="currentColor">
    <path d="M0 0h10v10H0z" fill="#f25022"/>
    <path d="M11 0h10v10H11z" fill="#00a4ef"/>
    <path d="M0 11h10v10H0z" fill="#ffb900"/>
    <path d="M11 11h10v10H11z" fill="#7fba00"/>
  </svg>
);

// =================================================================
// FACEBOOK ICON  
// =================================================================
const FacebookIcon = () => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="#1877f2">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

// =================================================================
// GOOGLE LOGIN BUTTON
// =================================================================
interface GoogleLoginButtonProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  text?: string;
  iconOnly?: boolean;
}

const GoogleLoginButton: React.FC<GoogleLoginButtonProps> = ({
  onSuccess,
  onError,
  text,
  iconOnly
}) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useTranslation();
  const buttonText = text ?? translate(t, TRANSLATIONS.CONTINUE_WITH_GOOGLE);

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setLoading(true);
      try {
        // Obtener ID token desde Google
        const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
        });
        
        if (!userInfoResponse.ok) {
          throw new Error(translate(t, TRANSLATIONS.OAUTH_GOOGLE_USERINFO_ERROR));
        }

        const response = await loginWithGoogle(tokenResponse.access_token);

        login(response.accessToken, response.refreshToken);
        message.success(translate(t, TRANSLATIONS.OAUTH_GOOGLE_SUCCESS));

        if (onSuccess) {
          onSuccess();
        }

        navigate('/dashboard');
      } catch (error: any) {
        console.error('Google login error:', error);
        message.error(error.message || translate(t, TRANSLATIONS.OAUTH_GOOGLE_ERROR_FALLBACK));

        if (onError) {
          onError(error.message);
        }
      } finally {
        setLoading(false);
      }
    },
    onError: (error) => {
      console.error('Google OAuth error:', error);
      message.error(translate(t, TRANSLATIONS.OAUTH_GOOGLE_ERROR_FALLBACK));

      if (onError) {
        onError(translate(t, TRANSLATIONS.OAUTH_GOOGLE_ERROR_FALLBACK));
      }
    },
  });

  return (
    <Button
      icon={<GoogleOutlined />}
      onClick={() => googleLogin()}
      loading={loading}
      block={!iconOnly}
      size="large"
      aria-label={iconOnly ? buttonText : undefined}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      {!iconOnly && buttonText}
    </Button>
  );
};

// =================================================================
// MICROSOFT LOGIN BUTTON
// =================================================================
interface MicrosoftLoginButtonProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  text?: string;
  iconOnly?: boolean;
}

const MicrosoftLoginButton: React.FC<MicrosoftLoginButtonProps> = ({
  onSuccess,
  onError,
  text,
  iconOnly
}) => {
  const [loading, setLoading] = useState(false);
  const { instance } = useMsal();
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useTranslation();
  const buttonText = text ?? translate(t, TRANSLATIONS.CONTINUE_WITH_MICROSOFT);

  const handleMicrosoftLogin = async () => {
    setLoading(true);
    try {
      const loginResponse = await instance.loginPopup({
        scopes: ['user.read'],
      });

      const response = await loginWithMicrosoft(loginResponse.accessToken);

      login(response.accessToken, response.refreshToken);
      message.success(translate(t, TRANSLATIONS.OAUTH_MICROSOFT_SUCCESS));

      if (onSuccess) {
        onSuccess();
      }

      navigate('/dashboard');
    } catch (error: any) {
      console.error('Microsoft login error:', error);
      message.error(error.message || translate(t, TRANSLATIONS.OAUTH_MICROSOFT_ERROR_FALLBACK));

      if (onError) {
        onError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      icon={<MicrosoftIcon />}
      onClick={handleMicrosoftLogin}
      loading={loading}
      block={!iconOnly}
      size="large"
      aria-label={iconOnly ? buttonText : undefined}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      {!iconOnly && buttonText}
    </Button>
  );
};

// =================================================================
// GITHUB LOGIN BUTTON
// =================================================================
interface GitHubLoginButtonProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  text?: string;
  iconOnly?: boolean;
}

const GitHubLoginButton: React.FC<GitHubLoginButtonProps> = ({
  text,
  iconOnly
}) => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const buttonText = text ?? translate(t, TRANSLATIONS.CONTINUE_WITH_GITHUB);

  const handleGitHubLogin = () => {
    setLoading(true);
    
    const authUrl = getGitHubAuthUrl();
    window.location.href = authUrl;
  };

  return (
    <Button
      icon={<GithubOutlined />}
      onClick={handleGitHubLogin}
      loading={loading}
      block={!iconOnly}
      size="large"
      aria-label={iconOnly ? buttonText : undefined}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      {!iconOnly && buttonText}
    </Button>
  );
};

// =================================================================
// FACEBOOK LOGIN BUTTON - ACTUALIZADO CON NUEVO PAQUETE
// =================================================================
interface FacebookLoginButtonProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  text?: string;
  iconOnly?: boolean;
}

const FacebookLoginButton: React.FC<FacebookLoginButtonProps> = ({
  onSuccess,
  onError,
  text,
  iconOnly
}) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useTranslation();
  const buttonText = text ?? translate(t, TRANSLATIONS.CONTINUE_WITH_FACEBOOK);

  const handleFacebookSuccess = async (response: any) => {
    setLoading(true);
    try {
      if (response.accessToken) {
        const result = await loginWithFacebook(response.accessToken);

        login(result.accessToken, result.refreshToken);
        message.success(translate(t, TRANSLATIONS.OAUTH_FACEBOOK_SUCCESS));

        if (onSuccess) {
          onSuccess();
        }

        navigate('/dashboard');
      }
    } catch (error: any) {
      console.error('Facebook login error:', error);
      message.error(error.message || translate(t, TRANSLATIONS.OAUTH_FACEBOOK_ERROR_FALLBACK));

      if (onError) {
        onError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFacebookError = (error: any) => {
    console.error('Facebook OAuth error:', error);
    message.error(translate(t, TRANSLATIONS.OAUTH_FACEBOOK_ERROR_FALLBACK));

    if (onError) {
      onError(translate(t, TRANSLATIONS.OAUTH_FACEBOOK_ERROR_FALLBACK));
    }
  };

  // El único de los cuatro proveedores que puede no estar configurado. Meta
  // exige verificación de empresa y revisión de la aplicación antes de que el
  // login funcione para alguien que no sea un probador de la propia app, así
  // que un entorno puede quedarse legítimamente sin FACEBOOK_APP_ID durante
  // mucho tiempo -- hoy mismo, sin ir más lejos.
  //
  // Sin esta guarda el botón se pinta igual, con appId vacío, y al pulsarlo
  // falla. Es la misma decisión que ya toma la API por su cuenta: Program.cs
  // sólo registra la verificación de Facebook si el secreto está en el Key
  // Vault, y VerifyFacebookTokenAsync rechaza los intentos si no. El front
  // era la única pieza que no se enteraba.
  const facebookAppId = process.env.REACT_APP_FACEBOOK_APP_ID;
  if (!facebookAppId) return null;

  return (
    <FacebookLogin
      appId={facebookAppId}
      onSuccess={handleFacebookSuccess}
      onFail={handleFacebookError}
      render={({ onClick }) => (
        <Button
          icon={<FacebookIcon />}
          onClick={onClick}
          loading={loading}
          block={!iconOnly}
          size="large"
          aria-label={iconOnly ? buttonText : undefined}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          {!iconOnly && buttonText}
        </Button>
      )}
    />
  );
};

// =================================================================
// OAUTH BUTTONS GROUP
// =================================================================
interface OAuthButtonsProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  showDivider?: boolean;
  iconOnly?: boolean;
}

const OAuthButtons: React.FC<OAuthButtonsProps> = ({
  onSuccess,
  onError,
  showDivider = true,
  iconOnly = false
}) => {
  const { t } = useTranslation();

  const buttons = (
    <>
      <GoogleLoginButton
        onSuccess={onSuccess}
        onError={onError}
        iconOnly={iconOnly}
      />

      <MicrosoftLoginButton
        onSuccess={onSuccess}
        onError={onError}
        iconOnly={iconOnly}
      />

      <GitHubLoginButton
        onSuccess={onSuccess}
        onError={onError}
        iconOnly={iconOnly}
      />

      <FacebookLoginButton
        onSuccess={onSuccess}
        onError={onError}
        iconOnly={iconOnly}
      />
    </>
  );

  return (
    <>
      {showDivider && (
        <Divider className={iconOnly ? 'heavyDutyMachinery-auth-divider' : undefined}>
          {translate(t, TRANSLATIONS.CONTINUE_WITH)}
        </Divider>
      )}

      {iconOnly ? (
        <div className="heavyDutyMachinery-oauth-row">{buttons}</div>
      ) : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {buttons}
        </Space>
      )}
    </>
  );
};

// =================================================================
// WRAPPER CON GOOGLE OAUTH PROVIDER
// =================================================================
interface OAuthButtonsWrapperProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  showDivider?: boolean;
  iconOnly?: boolean;
}

const OAuthButtonsWrapper: React.FC<OAuthButtonsWrapperProps> = (props) => {
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  if (!googleClientId) {
    console.warn('Google Client ID not configured');
  }

  return (
    <GoogleOAuthProvider clientId={googleClientId || ''}>
      <OAuthButtons {...props} />
    </GoogleOAuthProvider>
  );
};

export default OAuthButtonsWrapper;
export { 
  GoogleLoginButton, 
  MicrosoftLoginButton, 
  GitHubLoginButton,
  FacebookLoginButton 
};